import PagedSearchTable, { PagedTableFunctions } from '../../components/PagedSearchTable'
import React, { useContext, useRef, useState } from 'react'
import Dialog from '../../components/Dialog'
import { EditRow, EditTable, focusFirstInput } from '../../components/Fields'
import AppContext from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import LimeProductRequest from '../../controllers/LimeProductRequest'
import LimeProductType from '../../controllers/LimeProductType'
import LimingRegionResponse from '../../controllers/LimingRegionResponse'
import { useValidation } from '../../validation'
import { buildSetter } from '../../immutableState'
import Input from '../../components/Input'
import Number from '../../components/Number'
import NumberNullable from '../../components/NumberNullable'
import SelectString from '../../components/SelectString'
import { showSuccessOrFailed } from '../../Snacks'

const emptyProduct: LimeProductRequest = {
    id: 0,
    name: '',
    type: LimeProductType.Other,
    regionId: null,
    registrationNumber: null,
    supplier: null,
    pricePerTon: null,
    location: null,
    caPercent: 0,
    caCO3Percent: 0,
    caOPercent: 0,
    caSO4Percent: 0,
    mgPercent: 0,
    mgCO3Percent: 0,
    mgOPercent: 0,
    ccePercent: 0,
    sPercent: 0,
    pPercent: 0,
    kPercent: 0,
    ph: null,
    caMgRatio: null,
    fineness: null,
    active: true
}

function toForm(item: LimeProductResponse): LimeProductRequest {
    return {
        id: item.id,
        name: item.name,
        type: item.type,
        regionId: item.regionId,
        registrationNumber: item.registrationNumber,
        supplier: item.supplier,
        pricePerTon: item.pricePerTon,
        location: item.location,
        caPercent: item.caPercent,
        caCO3Percent: item.caCO3Percent,
        caOPercent: item.caOPercent,
        caSO4Percent: item.caSO4Percent,
        mgPercent: item.mgPercent,
        mgCO3Percent: item.mgCO3Percent,
        mgOPercent: item.mgOPercent,
        ccePercent: item.ccePercent,
        sPercent: item.sPercent,
        pPercent: item.pPercent,
        kPercent: item.kPercent,
        ph: item.ph,
        caMgRatio: item.caMgRatio,
        fineness: item.fineness,
        active: item.active
    }
}

const productTypes = Object.values(LimeProductType).map(t => ({ id: t, name: t }))

// Field visibility configuration based on product type
// Based on Excel spreadsheet analysis:
// - Calcite/Dolomite: Ca%, CaCO3%, Mg%, MgCO3%, CCE%, pH (carbonates)
// - Gypsum: Ca%, CaSO4%, S%, P%, CCE%, pH (sulfate)
// - Magnesium: Mg%, MgCO3%, CCE%, pH (no Ca)
// - Hydrated: Ca%, CaO%, Mg%, MgO%, CCE%, pH (oxides)
// - Potassium: K% only
// - Phosphate: P% only
// - Mix/Other: All fields
const getVisibleFields = (type: LimeProductType) => {
    switch (type) {
        case LimeProductType.Calcite:
        case LimeProductType.Dolomite:
            return {
                caPercent: true,
                caCO3Percent: true,
                caOPercent: false,
                caSO4Percent: false,
                mgPercent: true,
                mgCO3Percent: true,
                mgOPercent: false,
                ccePercent: true,
                sPercent: false,
                pPercent: false,
                kPercent: false,
                ph: true,
                caMgRatio: true,
                fineness: true
            }
        case LimeProductType.Gypsum:
            return {
                caPercent: true,
                caCO3Percent: false,
                caOPercent: false,
                caSO4Percent: true,
                mgPercent: false,
                mgCO3Percent: false,
                mgOPercent: false,
                ccePercent: true,
                sPercent: true,
                pPercent: true,
                kPercent: false,
                ph: true,
                caMgRatio: false,
                fineness: true
            }
        case LimeProductType.Magnesium:
            return {
                caPercent: false,
                caCO3Percent: false,
                caOPercent: false,
                caSO4Percent: false,
                mgPercent: true,
                mgCO3Percent: true,
                mgOPercent: false,
                ccePercent: true,
                sPercent: false,
                pPercent: false,
                kPercent: false,
                ph: true,
                caMgRatio: false,
                fineness: true
            }
        case LimeProductType.Hydrated:
            return {
                caPercent: true,
                caCO3Percent: false,
                caOPercent: true,
                caSO4Percent: false,
                mgPercent: true,
                mgCO3Percent: false,
                mgOPercent: true,
                ccePercent: true,
                sPercent: false,
                pPercent: false,
                kPercent: false,
                ph: true,
                caMgRatio: true,
                fineness: true
            }
        case LimeProductType.Potassium:
            return {
                caPercent: false,
                caCO3Percent: false,
                caOPercent: false,
                caSO4Percent: false,
                mgPercent: false,
                mgCO3Percent: false,
                mgOPercent: false,
                ccePercent: false,
                sPercent: false,
                pPercent: false,
                kPercent: true,
                ph: false,
                caMgRatio: false,
                fineness: false
            }
        case LimeProductType.Phosphate:
            return {
                caPercent: false,
                caCO3Percent: false,
                caOPercent: false,
                caSO4Percent: false,
                mgPercent: false,
                mgCO3Percent: false,
                mgOPercent: false,
                ccePercent: false,
                sPercent: false,
                pPercent: true,
                kPercent: false,
                ph: false,
                caMgRatio: false,
                fineness: false
            }
        case LimeProductType.Mix:
        case LimeProductType.Other:
        default:
            return {
                caPercent: true,
                caCO3Percent: true,
                caOPercent: true,
                caSO4Percent: true,
                mgPercent: true,
                mgCO3Percent: true,
                mgOPercent: true,
                ccePercent: true,
                sPercent: true,
                pPercent: true,
                kPercent: true,
                ph: true,
                caMgRatio: true,
                fineness: true
            }
    }
}

// Display value or dash if not applicable to the product type
const displayField = (item: LimeProductResponse, field: keyof ReturnType<typeof getVisibleFields>, value: number | null): string => {
    const fields = getVisibleFields(item.type)
    if (!fields[field]) return '-'
    return value?.toString() ?? '-'
}

const LimingProducts: React.FC = () => {
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<LimeProductResponse>>()
    const [show, setShow] = useState(false)
    const [upsertData, setUpsertData] = useState<LimeProductRequest>(emptyProduct)
    const setData = buildSetter(upsertData, setUpsertData)
    const [regions, setRegions] = useState<LimingRegionResponse[]>([])

    React.useEffect(() => {
        LimeController.getRegions().then(setRegions)
    }, [])

    const visibleFields = getVisibleFields(upsertData.type)

    function showPopup(preset: LimeProductRequest) {
        setUpsertData(preset)
        setShow(true)
    }

    function upsertCallback(data: LimeProductRequest) {
        if (!validation.validate()) { return }

        showSuccessOrFailed(
            context,
            LimeController.productUpsert(data),
            data.id === 0 ? context.word('product_added') : context.word('product_updated')
        ).then(() => {
            pagedTableRef.current?.refresh()
            setShow(false)
        })
    }

    function setActive(id: number, state: boolean) {
        showSuccessOrFailed(context, LimeController.productToggleActive({
            id,
            active: state
        })).then(() => {
            pagedTableRef.current?.refresh()
        })
    }

    const strikeOut = (product: LimeProductResponse): string => {
        return !product.active ? 'text-red-500 italic line-through' : ''
    }

    const validation = useValidation({
        nameRequired: () => upsertData.name.length > 0
    })

    return (
        <>
            <Dialog
                mounted={focusFirstInput}
                title={upsertData.id === 0 ? context.word('add_product') : context.word('edit_product')}
                show={show}
                setShow={setShow}
                body={
                    <EditTable
                        save={() => upsertCallback(upsertData)}
                        discard={() => setShow(false)}
                        saveWord={upsertData.id === 0 ? 'insert' : 'update'}
                    >
                        {EditRow(
                            context.word('name'),
                            <Input value={upsertData.name} change={v => setData({ name: v })} />,
                            validation.rules.nameRequired,
                            context.word('name_required')
                        )}
                        {EditRow(
                            context.word('type'),
                            <SelectString
                                options={productTypes}
                                textFunc={t => t.name}
                                valueFunc={t => t.id}
                                value={upsertData.type}
                                onChange={v => setData({ type: v as LimeProductType })}
                            />
                        )}
                        {EditRow(
                            context.word('region'),
                            <select
                                className="input w-full"
                                value={upsertData.regionId ?? ''}
                                onChange={e => setData({ regionId: e.target.value ? parseInt(e.target.value) : null })}
                            >
                                <option value="">{context.word('all_regions')}</option>
                                {regions.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                        )}
                        {EditRow(
                            context.word('registration_number'),
                            <Input value={upsertData.registrationNumber ?? ''} change={v => setData({ registrationNumber: v || null })} />
                        )}
                        {EditRow(
                            context.word('supplier'),
                            <Input value={upsertData.supplier ?? ''} change={v => setData({ supplier: v || null })} />
                        )}
                        {EditRow(
                            context.word('price_per_ton'),
                            <NumberNullable value={upsertData.pricePerTon} change={v => setData({ pricePerTon: v })} />
                        )}
                        {EditRow(
                            context.word('location'),
                            <Input value={upsertData.location ?? ''} change={v => setData({ location: v || null })} />
                        )}

                        {/* Chemical composition fields - conditional based on product type */}
                        {visibleFields.caPercent && EditRow(
                            '% Ca',
                            <Number value={upsertData.caPercent} change={v => setData({ caPercent: v })} />
                        )}
                        {visibleFields.caCO3Percent && EditRow(
                            '% CaCO3',
                            <Number value={upsertData.caCO3Percent} change={v => setData({ caCO3Percent: v })} />
                        )}
                        {visibleFields.caOPercent && EditRow(
                            '% CaO',
                            <Number value={upsertData.caOPercent} change={v => setData({ caOPercent: v })} />
                        )}
                        {visibleFields.caSO4Percent && EditRow(
                            '% CaSO4',
                            <Number value={upsertData.caSO4Percent} change={v => setData({ caSO4Percent: v })} />
                        )}
                        {visibleFields.mgPercent && EditRow(
                            '% Mg',
                            <Number value={upsertData.mgPercent} change={v => setData({ mgPercent: v })} />
                        )}
                        {visibleFields.mgCO3Percent && EditRow(
                            '% MgCO3',
                            <Number value={upsertData.mgCO3Percent} change={v => setData({ mgCO3Percent: v })} />
                        )}
                        {visibleFields.mgOPercent && EditRow(
                            '% MgO',
                            <Number value={upsertData.mgOPercent} change={v => setData({ mgOPercent: v })} />
                        )}
                        {visibleFields.ccePercent && EditRow(
                            '% CCE',
                            <Number value={upsertData.ccePercent} change={v => setData({ ccePercent: v })} />
                        )}
                        {visibleFields.sPercent && EditRow(
                            '% S',
                            <Number value={upsertData.sPercent} change={v => setData({ sPercent: v })} />
                        )}
                        {visibleFields.pPercent && EditRow(
                            '% P',
                            <Number value={upsertData.pPercent} change={v => setData({ pPercent: v })} />
                        )}
                        {visibleFields.kPercent && EditRow(
                            '% K',
                            <Number value={upsertData.kPercent} change={v => setData({ kPercent: v })} />
                        )}
                        {visibleFields.ph && EditRow(
                            'pH',
                            <NumberNullable value={upsertData.ph} change={v => setData({ ph: v })} />
                        )}
                        {visibleFields.caMgRatio && EditRow(
                            'Ca:Mg Ratio',
                            <NumberNullable value={upsertData.caMgRatio} change={v => setData({ caMgRatio: v })} />
                        )}
                        {visibleFields.fineness && EditRow(
                            context.word('fineness'),
                            <NumberNullable value={upsertData.fineness} change={v => setData({ fineness: v })} />
                        )}
                    </EditTable>
                }
            />

            <div className="btn bg-primary m-2" onClick={() => showPopup(emptyProduct)}>
                {context.word('new_product')}
            </div>

            <PagedSearchTable<LimeProductResponse>
                componentRef={pagedTableRef}
                call={LimeController.productsPaged}
                columns={[
                    { header: context.word('name'), row: item => <div className={strikeOut(item)}>{item.name}</div> },
                    { header: context.word('type'), row: item => <div className={strikeOut(item)}>{item.type}</div> },
                    { header: context.word('region'), row: item => <div className={strikeOut(item)}>{item.regionName ?? context.word('all_regions')}</div> },
                    { header: '% Ca', row: item => <div className={strikeOut(item)}>{displayField(item, 'caPercent', item.caPercent)}</div> },
                    { header: '% CaCO3', row: item => <div className={strikeOut(item)}>{displayField(item, 'caCO3Percent', item.caCO3Percent)}</div> },
                    { header: '% CaO', row: item => <div className={strikeOut(item)}>{displayField(item, 'caOPercent', item.caOPercent)}</div> },
                    { header: '% CaSO4', row: item => <div className={strikeOut(item)}>{displayField(item, 'caSO4Percent', item.caSO4Percent)}</div> },
                    { header: '% Mg', row: item => <div className={strikeOut(item)}>{displayField(item, 'mgPercent', item.mgPercent)}</div> },
                    { header: '% MgCO3', row: item => <div className={strikeOut(item)}>{displayField(item, 'mgCO3Percent', item.mgCO3Percent)}</div> },
                    { header: '% MgO', row: item => <div className={strikeOut(item)}>{displayField(item, 'mgOPercent', item.mgOPercent)}</div> },
                    { header: '% CCE', row: item => <div className={strikeOut(item)}>{displayField(item, 'ccePercent', item.ccePercent)}</div> },
                    { header: '% S', row: item => <div className={strikeOut(item)}>{displayField(item, 'sPercent', item.sPercent)}</div> },
                    { header: '% P', row: item => <div className={strikeOut(item)}>{displayField(item, 'pPercent', item.pPercent)}</div> },
                    { header: '% K', row: item => <div className={strikeOut(item)}>{displayField(item, 'kPercent', item.kPercent)}</div> },
                    { header: 'pH', row: item => <div className={strikeOut(item)}>{displayField(item, 'ph', item.ph)}</div> },
                    { header: 'Ca:Mg', row: item => <div className={strikeOut(item)}>{displayField(item, 'caMgRatio', item.caMgRatio)}</div> },
                    { header: context.word('fineness'), row: item => <div className={strikeOut(item)}>{displayField(item, 'fineness', item.fineness)}</div> },
                    {
                        header: context.word('action'),
                        row: item => (
                            <div className="flex gap-2">
                                <span
                                    className="underline cursor-pointer text-primary-600"
                                    onClick={() => showPopup(toForm(item))}
                                >
                                    {context.word('edit')}
                                </span>
                                <span
                                    className="underline cursor-pointer"
                                    onClick={() => setActive(item.id, !item.active)}
                                >
                                    {item.active ? context.word('deactivate') : context.word('activate')}
                                </span>
                            </div>
                        )
                    }
                ]}
                keyExtractor={i => i.id}
            />
        </>
    )
}

export default LimingProducts
